import { useEffect, useState } from "react";
import { X, ChevronDown } from "lucide-react";
import { publicApi } from "@/api";
import SearchSelect from "@/components/SearchSelect";

const labelCls = "text-sm font-semibold text-slate-700 mb-2 block";

const FAQS = [
  ["¿Cómo puedo pagar?", "El pago es únicamente por MercadoPago. Si necesitás pagar de otra forma, envianos un mensaje de WhatsApp."],
  ["¿Puedo contratar seguro de robo?", "Sí, podés contratar seguro con cobertura de robo. Luego de pagar tenés que ingresar a la app de ATM Seguros, ir al área de inspecciones y realizar la inspección de tu moto. Una vez que ATM Seguros apruebe las fotos de tu inspección, tu póliza ya tiene cobertura de robo."],
  ["¿Me entregan mi póliza después de pagar?", "Luego de pagar ya podés ingresar a la app de ATM Seguros y descargar tu póliza."],
  ["¿Desde cuándo tengo cobertura?", "La póliza indica arriba, del lado derecho, la fecha y hora de inicio de cobertura."],
  ["¿Puedo contratar el seguro si no soy titular de la moto?", "Sí, podés contratar el seguro. Sin embargo, en coberturas como la de robo, si ocurre un siniestro el titular debe solicitar la baja en el Registro de Motovehículos para poder abonar el siniestro de robo."],
  ["¿Cuál es la compañía de seguros que emite mi póliza?", "La póliza es emitida por ATM Seguros. Nosotros somos Productor Asesor de Seguros de la compañía."],
  ["¿Cómo puedo contactarme?", "Al pie de página tenés un formulario de contacto. También podés enviarnos un WhatsApp o un email a contacto@segurosparamoto.ar."],
];

function Faq() {
  const [abierto, setAbierto] = useState(null);
  return (
    <div className="mt-10" data-testid="faq-section">
      <h2 className="font-heading text-xl font-bold text-slate-900 mb-4">Preguntas frecuentes</h2>
      <div className="space-y-2">
        {FAQS.map(([q, a], i) => (
          <div key={i} className="border border-slate-200 rounded-[20px] bg-white overflow-hidden shadow-sm" data-testid={`faq-item-${i}`}>
            <button type="button" data-testid={`faq-toggle-${i}`} onClick={() => setAbierto(abierto === i ? null : i)}
              className="w-full flex items-center justify-between gap-3 p-4 text-left">
              <span className="font-heading font-semibold text-sm text-slate-900">{q}</span>
              <ChevronDown className={`w-5 h-5 text-slate-400 shrink-0 transition-transform ${abierto === i ? "rotate-180" : ""}`} />
            </button>
            {abierto === i && <p className="px-4 pb-4 -mt-1 text-sm text-slate-600 leading-relaxed" data-testid={`faq-answer-${i}`}>{a}</p>}
          </div>
        ))}
      </div>
    </div>
  );
}

export default function MotoStep({ boot, value, onChange, onNext, onBack }) {
  const [modelos, setModelos] = useState([]);
  const [loadingModelos, setLoadingModelos] = useState(false);
  const [showComo, setShowComo] = useState(false);
  const [comoVisto, setComoVisto] = useState(() => localStorage.getItem("spm_como_visto") === "1");
  const abrirComo = () => {
    setShowComo(true);
    if (!comoVisto) {
      localStorage.setItem("spm_como_visto", "1");
      setComoVisto(true);
    }
  };
  const years = [];
  for (let y = boot.anio_max; y >= boot.anio_min; y--) years.push(y);

  useEffect(() => {
    if (!value.marca) { setModelos([]); return; }
    setLoadingModelos(true);
    publicApi.get(`/public/params/modelos?marca=${value.marca}&tipo_uso=${value.tipo_uso}`)
      .then(({ data }) => setModelos(data))
      .catch(() => setModelos([]))
      .finally(() => setLoadingModelos(false));
  }, [value.marca, value.tipo_uso]);

  const set = (k, v) => onChange({ ...value, [k]: v });
  const valid = value.marca && value.modelo && value.anio && (Number(value.tipo_uso) === 1 || value.comercial_consent);

  return (
    <div data-testid="step-moto">
      <div className="rounded-3xl overflow-hidden mb-5 relative">
        <img src={boot.branding.hero_image} alt="Portada" className="w-full h-40 sm:h-48 object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-900/85 via-slate-900/30 to-transparent flex items-end p-5">
          <div>
            <h1 className="text-white font-heading font-bold text-2xl sm:text-3xl leading-tight whitespace-pre-line" data-testid="hero-title">{boot.branding.hero_titulo}</h1>
            <p className="text-white/85 text-sm mt-1" data-testid="hero-subtitle">{boot.branding.hero_subtitulo}</p>
          </div>
        </div>
      </div>
      {!boot.tablas_cargadas && (
        <p className="text-amber-700 bg-amber-50 border border-amber-200 rounded-xl p-3 text-sm mb-4" data-testid="tablas-warning">
          Estamos actualizando las listas de motos. Si no encontras la tuya, intenta en unos minutos.
        </p>
      )}
      <div className="flex items-center justify-between gap-3 mb-1">
        <h2 className="font-heading text-base sm:text-xl font-bold text-slate-900 min-w-0">Contanos sobre tu moto</h2>
        <button type="button" data-testid="como-funciona-button" onClick={abrirComo}
          className={`text-sm font-semibold text-emerald-700 border border-emerald-200 bg-emerald-50 rounded-full px-4 py-2 shrink-0 ${comoVisto ? "" : "animate-shake"}`}>
          ¿Como funciona?
        </button>
      </div>
      <p className="text-slate-500 text-sm mb-5">Con estos datos buscamos el precio exacto para vos.</p>
      <div className="space-y-5">
        <SearchSelect label="Marca" htmlFor="marca" testid="marca-select" placeholder="Elegi o busca la marca"
          options={boot.marcas.map((m) => ({ value: m.codigo, label: m.descripcion }))}
          value={value.marca}
          onChange={(o) => onChange({ ...value, marca: o.value, marca_desc: o.label, modelo: "", modelo_desc: "" })} />
        <SearchSelect label="Año de patentamiento" htmlFor="anio" testid="anio-select" placeholder="Elegi el año"
          options={[{ value: "0KM", label: "Cero Kilómetro (sin rodar)" }, ...years.map((y) => ({ value: String(y), label: String(y) }))]}
          value={value.sin_rodar ? "0KM" : String(value.anio || "")}
          onChange={(o) => onChange(o.value === "0KM" ? { ...value, anio: String(boot.anio_max), sin_rodar: true } : { ...value, anio: o.value, sin_rodar: false })} />
        {value.sin_rodar && (
          <p className="text-sm text-slate-700 bg-emerald-50 border border-emerald-200 rounded-[20px] p-4" data-testid="sin-rodar-aviso-moto">
            <b>Moto 0km ({boot.anio_max}).</b> Si elegis una cobertura contra robo, deberas tener el <b>certificado de no rodamiento</b>.
          </p>
        )}
        <SearchSelect label="Modelo" htmlFor="modelo" testid="modelo-select"
          placeholder={!value.marca ? "Primero elegi la marca" : "Elegi o busca el modelo"}
          options={modelos.map((m) => ({ value: m.cod_model, label: m.modelo, tau_codia: m.tau_codia }))}
          value={value.modelo} disabled={!value.marca} loading={loadingModelos}
          onChange={(o) => onChange({ ...value, modelo: o.value, modelo_desc: o.label, tau_codia: o.tau_codia || "" })} />
        <div>
          <span className={labelCls}>¿Para que la usas?</span>
          <div className="grid grid-cols-2 gap-3">
            {[{ v: 1, t: "Particular", d: "Uso personal" }, { v: 2, t: "Comercial", d: "Ej: delivery o mensajeria" }].map((o) => (
              <button key={o.v} type="button" data-testid={`tipo-uso-${o.v}`}
                onClick={() => onChange({ ...value, tipo_uso: o.v, comercial_consent: o.v === 2 ? value.comercial_consent : false })}
                className={`h-16 rounded-2xl border-2 text-left px-4 transition ${Number(value.tipo_uso) === o.v ? "border-emerald-600 bg-emerald-50" : "border-slate-200 bg-white"}`}>
                <p className="font-heading font-semibold text-slate-900">{o.t}</p>
                <p className="text-xs text-slate-500">{o.d}</p>
              </button>
            ))}
          </div>
        </div>
        {Number(value.tipo_uso) === 2 && (
          <label className="flex items-start gap-3 bg-amber-50 border border-amber-300 rounded-[20px] p-4 cursor-pointer" data-testid="comercial-warning">
            <input type="checkbox" data-testid="comercial-consent" className="mt-1 w-5 h-5 accent-emerald-600 shrink-0" checked={Boolean(value.comercial_consent)} onChange={(e) => set("comercial_consent", e.target.checked)} />
            <span className="text-sm text-amber-900"><b>Importante:</b> ATM Seguros no cubre el trabajo en apps de transporte de personas. Confirmo que mi moto no se usa para ese fin.</span>
          </label>
        )}
      </div>
      <button data-testid="moto-next-button" disabled={!valid} onClick={onNext}
        className="w-full h-14 mt-6 rounded-[14px] bg-[#1C1C1E] hover:bg-zinc-800 disabled:bg-slate-300 text-white font-heading font-semibold text-lg transition">
        Continuar
      </button>
      <Faq />
      <p className="text-center text-xs text-slate-400 mt-8 mb-10" data-testid="ssn-line">{boot.branding.empresa} - Productor Asesor de Seguros - Matricula SSN: 89459. Las Pólizas son emitidas por ATM Seguros.</p>
      {showComo && (
        <div className="fixed inset-0 z-[60] bg-black/50 flex items-end sm:items-center justify-center" onClick={() => setShowComo(false)} data-testid="como-funciona-modal">
          <div className="bg-white rounded-t-[28px] sm:rounded-[24px] w-full max-w-lg max-h-[85vh] overflow-y-auto p-6 shadow-[0_24px_60px_rgba(0,0,0,0.25)]" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-start gap-3 mb-4">
              <h3 className="font-heading font-bold text-xl text-slate-900">¿Como funciona?</h3>
              <button data-testid="como-funciona-close" onClick={() => setShowComo(false)} className="p-2 -m-1 text-slate-400 shrink-0" aria-label="Cerrar"><X className="w-6 h-6" /></button>
            </div>
            <ol className="space-y-4">
              {[
                ["Informa los datos de tu moto", "Marca, año y modelo. Con eso buscamos el precio exacto para vos."],
                ["Elegi tu cobertura", "Ves los precios en vivo de ATM Seguros y que incluye cada una."],
                ["Completa tus datos", "Solo lo necesario para emitir la poliza a tu nombre."],
                ["Confirma y recibis el link de MercadoPago", "Pagas de forma segura con el medio que prefieras."],
                ["Si contrataste cobertura contra robo", "Haces la inspeccion de tu moto desde la app de ATM Seguros."],
                ["Si contrataste Responsabilidad Civil", "Luego del pago bajas la app de ATM Seguros y ya accedes a tu poliza."],
              ].map(([t, d], i) => (
                <li key={i} className="flex gap-3">
                  <span className="w-7 h-7 rounded-full bg-emerald-600 text-white font-heading font-bold text-sm flex items-center justify-center shrink-0">{i + 1}</span>
                  <div>
                    <p className="font-semibold text-slate-800 text-sm">{t}</p>
                    <p className="text-sm text-slate-500">{d}</p>
                  </div>
                </li>
              ))}
            </ol>
            <button data-testid="como-funciona-ok" onClick={() => setShowComo(false)} className="w-full h-12 mt-6 rounded-[14px] bg-[#1C1C1E] hover:bg-zinc-800 text-white font-heading font-semibold">Entendido</button>
          </div>
        </div>
      )}
    </div>
  );
}
